import React from 'react';
import { motion } from 'framer-motion';
import { FaChartLine, FaShoppingCart, FaUsers, FaMoneyBillWave, FaCalendarAlt, FaSpinner } from 'react-icons/fa';
import styled from 'styled-components';

const ContentArea = styled(motion.div)`
  background: linear-gradient(135deg, rgba(255, 255, 255, 0.08) 0%, rgba(255, 255, 255, 0.03) 100%);
  padding: 2rem;
  border-radius: 20px;
  margin-bottom: 2rem;
  box-shadow: 0 8px 25px rgba(0, 0, 0, 0.2);
  border: 1px solid rgba(255, 255, 255, 0.08);
  backdrop-filter: blur(10px);
  position: relative;
  overflow: hidden;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(240px, 1fr));
  gap: 1.5rem;
  margin-top: 1.5rem;
`;

const StatCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  transition: all 0.3s ease;
  display: flex;
  flex-direction: column;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.2);
    border-color: rgba(74, 144, 226, 0.3);
  }
`;

const StatIcon = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 12px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 1rem;
  font-size: 1.5rem;
  background: ${props => props.background || 'rgba(74, 144, 226, 0.2)'};
  color: ${props => props.color || '#4a90e2'};
`;

const StatTitle = styled.div`
  font-size: 0.9rem;
  color: rgba(255, 255, 255, 0.7);
  margin-bottom: 0.5rem;
`;

const StatValue = styled.div`
  font-size: 1.8rem;
  font-weight: 600;
  color: white;
  margin-bottom: 0.5rem;
`;

const StatChange = styled.div`
  font-size: 0.8rem;
  color: ${props => props.positive ? '#4caf50' : '#f44336'};
  display: flex;
  align-items: center;
  margin-top: auto;
`;

const ChartContainer = styled(motion.div)`
  background: rgba(255, 255, 255, 0.05);
  border-radius: 15px;
  padding: 1.5rem;
  border: 1px solid rgba(255, 255, 255, 0.1);
  margin-top: 2rem;
  height: 300px;
  position: relative;
`;

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, y: 20 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      type: "spring",
      stiffness: 100,
      damping: 15
    }
  }
};

const AnalyticsTab = ({ earnings, products, events, loading }) => {
  // Calculate some stats based on available data
  const totalSales = products.reduce((total, product) => total + (product.downloads || 0), 0);
  const totalEvents = events.length;
  const totalAttendees = events.reduce((total, event) => total + (event.attendees?.length || 0), 0);
  
  // Mock data for chart visualization
  const mockChartData = (
    <div style={{ 
      width: '100%', 
      height: '100%', 
      display: 'flex', 
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'center',
      color: 'rgba(255,255,255,0.7)'
    }}>
      <FaChartLine style={{ fontSize: '3rem', marginBottom: '1rem', color: '#4a90e2' }} />
      <p>Interactive charts will be displayed here</p>
      <p style={{ fontSize: '0.9rem', marginTop: '0.5rem' }}>
        Showing performance data for the last 30 days
      </p>
    </div>
  );
  
  return (
    <ContentArea
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <div style={{ 
        display: 'flex', 
        justifyContent: 'space-between', 
        alignItems: 'center',
        marginBottom: '1rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h2 style={{ margin: 0, color: 'white' }}>Analytics Dashboard</h2>
        <div style={{ 
          background: 'rgba(255,255,255,0.1)', 
          padding: '0.5rem 1rem', 
          borderRadius: '8px',
          fontSize: '0.9rem',
          color: 'rgba(255,255,255,0.8)',
          display: 'flex',
          alignItems: 'center'
        }}>
          <FaCalendarAlt style={{ marginRight: '0.5rem' }} />
          Last 30 Days
        </div>
      </div>
      
      {loading ? (
        <div style={{ textAlign: 'center', padding: '3rem' }}>
          <FaSpinner style={{ 
            fontSize: '2rem', 
            color: '#4a90e2',
            animation: 'spin 1s linear infinite'
          }} />
          <p style={{ marginTop: '1rem', color: 'rgba(255,255,255,0.7)' }}>
            Loading analytics data...
          </p>
          <style>{`
            @keyframes spin {
              0% { transform: rotate(0deg); }
              100% { transform: rotate(360deg); }
            }
          `}</style>
        </div>
      ) : (
        <>
          <StatsGrid
            as={motion.div}
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {/* Total Earnings Stat */}
            <StatCard variants={itemVariants}>
              <StatIcon background="rgba(74, 144, 226, 0.2)" color="#4a90e2">
                <FaMoneyBillWave />
              </StatIcon>
              <StatTitle>Total Earnings</StatTitle>
              <StatValue>${earnings.total.toFixed(2)}</StatValue>
              <StatChange positive={true}>
                +{(earnings.total * 0.15).toFixed(2)} from last month
              </StatChange>
            </StatCard>
            
            {/* Available Balance Stat */}
            <StatCard variants={itemVariants}>
              <StatIcon background="rgba(76, 175, 80, 0.2)" color="#4caf50">
                <FaMoneyBillWave />
              </StatIcon>
              <StatTitle>Available Balance</StatTitle>
              <StatValue>${earnings.available.toFixed(2)}</StatValue>
              <StatChange positive={true}>
                Ready to withdraw
              </StatChange>
            </StatCard>
            
            {/* Total Sales Stat */}
            <StatCard variants={itemVariants}>
              <StatIcon background="rgba(156, 39, 176, 0.2)" color="#9c27b0">
                <FaShoppingCart />
              </StatIcon>
              <StatTitle>Total Sales</StatTitle>
              <StatValue>{totalSales}</StatValue>
              <StatChange positive={totalSales > 0}>
                {totalSales > 0 ? `+${Math.floor(totalSales * 0.2)} from last month` : 'No sales yet'}
              </StatChange>
            </StatCard>
            
            {/* Total Attendees Stat */}
            <StatCard variants={itemVariants}>
              <StatIcon background="rgba(255, 152, 0, 0.2)" color="#ff9800">
                <FaUsers />
              </StatIcon>
              <StatTitle>Event Attendees</StatTitle>
              <StatValue>{totalAttendees}</StatValue>
              <StatChange positive={totalAttendees > 0}>
                {totalAttendees > 0 ? `Across ${totalEvents} events` : 'No attendees yet'}
              </StatChange>
            </StatCard>
          </StatsGrid>
          
          {/* Sales Performance Chart */}
          <ChartContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <h3 style={{ margin: '0 0 1.5rem 0', color: 'white' }}>Sales Performance</h3>
            {mockChartData}
          </ChartContainer>
          
          {/* Product Performance Chart */}
          <ChartContainer
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
          >
            <h3 style={{ margin: '0 0 1.5rem 0', color: 'white' }}>Product Performance</h3>
            {mockChartData}
          </ChartContainer>
        </>
      )}
    </ContentArea>
  );
};

export default AnalyticsTab;
